// Per-stock Trading Range chart selections, one localStorage key:
// { [symbol]: { sma, range, auto, strategy } }. Shared between the chart
// itself and useWatchBacktests (services/api.js), which mirrors each watch's
// stats over the SAME range the user sees on that stock's chart — computing
// them over a fixed window made the dialog contradict the chart.
import { STRATEGIES } from '../constants/signalStrategies';
import { SMA_PERIODS } from '../shared/bandSignals';

export { SMA_PERIODS };
export const RANGES = [
  { label: '1M', days: 21 },
  { label: '3M', days: 63 },
  { label: '6M', days: 126 },
  { label: '1Y', days: 252 },
];

// Chart-only pseudo-strategy: backtest every shape and show the winner. Never
// leaves the client — saved watches always carry the RESOLVED shape, so
// fp_user validation and the signal-watch worker only ever see concrete keys.
export const AUTO_STRATEGY = 'auto';

export const DEFAULT_SMA = 10;
// 3M since issue #472: fit once and held, a 3-month ranking window beat 6M per
// ticker in the 2022→ backtest (55/34) and tied from 2024 (REFIT_STUDY.md). It
// is also fp_user's default lookback_days and the server refit's fallback.
export const DEFAULT_RANGE = '3M';
const SETTINGS_KEY = 'bollingerChartSettings';

export const rangeDaysFor = (label) =>
  (RANGES.find((r) => r.label === label) || RANGES.find((r) => r.label === DEFAULT_RANGE)).days;

// The inverse, for a saved watch's lookback_days ("6M"); null for a length no range offers.
export const rangeLabelFor = (days) => RANGES.find((r) => r.days === days)?.label ?? null;

export const loadChartSettings = (symbol) => {
  let saved = null;
  try {
    saved = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}')[symbol];
  } catch { /* corrupt storage — fall back to defaults */ }
  const hasSavedSma = SMA_PERIODS.includes(saved?.sma);
  return {
    sma: hasSavedSma ? saved.sma : DEFAULT_SMA,
    range: RANGES.some((r) => r.label === saved?.range) ? saved.range : DEFAULT_RANGE,
    // Stocks where the user already picked a period stay manual; untouched stocks auto-fit
    auto: typeof saved?.auto === 'boolean' ? saved.auto : !hasSavedSma,
    // Untouched stocks auto-fit the shape too; an explicit pick sticks
    strategy: saved?.strategy === AUTO_STRATEGY || STRATEGIES.some((s) => s.key === saved?.strategy)
      ? saved.strategy : AUTO_STRATEGY,
  };
};

export const saveChartSettings = (symbol, settings) => {
  try {
    const all = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}');
    all[symbol] = settings;
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(all));
  } catch { /* quota/corrupt storage — selection still applies for this session */ }
};
